import { GRAPHQL_AUTH_MODE } from "@aws-amplify/api";
import { API } from "aws-amplify";
import { useDispatch, useSelector } from "react-redux";
import {
  setListing,
  setSearchText,
  setSelected,
} from "../store/ducks/orderedItem";
import { HeadCell } from "../models/dataTable";
import useApp from "./useApp";
import useAccount from "./useAccount";
import useConcept from "./useConcept";
import useGroup from "./useGroup";
import { GLOBAL_GUEST_ID } from "../constants/enums";
import { ListingVariables } from "../models/app";
import { listUsers } from "../graphql/queries";
import { listOrderedItems } from "../graphql/queries";
import { formatDateToYYYYMMDDHHMMSS } from "../helpers/utils";

const useResource = (listingName: string, singleName: string) => {
  const dispatch = useDispatch();
  const { showError, session } = useApp();
  const { accountsSelected } = useAccount("accounts", "account");
  const { conceptsSelected, conceptsSelectedFilters } = useConcept(
    "concepts",
    "concept",
  );
  const { groupsGetName } = useGroup("groups", "group");

  const listing = useSelector((state: any) => {
    return state.orderedItems.listing;
  });

  const searchText = useSelector((state: any) => {
    return state.orderedItems.searchText;
  });

  async function listTopCustomers(params: any) {
    const { startIndex, limit } = params;

    try {
      const filter: any = {
        deleted: { eq: "0" },
        accountID: { eq: accountsSelected.id },
      };

      // Fetch all customers with pagination
      let allListing: any[] = [];
      let nextToken: string | null = null;
      let fetchLimit = 10000; // Fetch in large batches
      let attempts = 0;
      const maxAttempts = 50; // Prevent infinite loops

      do {
        const response: any = await API.graphql({
          query: listUsers,
          variables: {
            filter,
            limit: fetchLimit,
            nextToken: nextToken,
          },
          authMode: session
            ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
            : GRAPHQL_AUTH_MODE.AWS_IAM,
        });

        const items = response.data.listUsers.items || [];
        allListing = [...allListing, ...items];
        nextToken = response.data.listUsers.nextToken;
        attempts++;

      } while (nextToken && attempts < maxAttempts);

      // Sort by orders_count descending (client-side since GraphQL doesn't support sort)
      allListing = allListing.sort((a: any, b: any) => {
        const countA = a.orders_count || 0;
        const countB = b.orders_count || 0;
        return countB - countA;
      });

      // Apply pagination client-side
      if (startIndex > 0 || limit) {
        const start = startIndex || 0;
        const end = limit ? start + limit : undefined;
        allListing = allListing.slice(start, end);
      }

      return allListing;
    } catch (err: Error | any) {
      showError(err);
      return [];
    }
  }

  async function listUserOrderedItemsCount(params: any) {
    try {
      const filter: any = {
        deleted: { eq: "0" },
        userID: { eq: params.userId },
      };

      const response: any = await API.graphql({
        query: listOrderedItems,
        variables: {
          filter,
          limit: 10000,
          nextToken: null,
        },
        authMode: session
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      let listing = response.data.listOrderedItems.items;

      // Sort by count descending (client-side since GraphQL doesn't support sort)
      listing = listing.sort((a: any, b: any) => {
        const countA = a.count || 0;
        const countB = b.count || 0;
        return countB - countA;
      });

      return listing;
    } catch (err: Error | any) {
      showError(err);
      return [];
    }
  }

  async function listTopOrderedItems(params: any) {
    try {
      // Fetch all items for the userID without conceptID filter
      // We'll filter by conceptID client-side to allow global items (isGlobal="1") to pass through
      let filter: any = {
        deleted: { eq: "0" },
        userID: { eq: params.userId },
      };

      // Don't filter by conceptID in GraphQL query - we need to fetch all items
      // to include global items that might have different conceptIDs
      // All conceptID filtering will be done client-side


      let response: any = await API.graphql({
        query: listOrderedItems,
        variables: {
          filter,
          limit: params.limit || 10000,
          nextToken: null,
        },
        authMode: session
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      let listing = response.data.listOrderedItems.items;

      // If no items found with userID, try fetching all OrderedItems (without userID filter)
      // This helps us see if OrderedItems exist at all, or if they're stored with different userID
      if (listing.length === 0) {
        const filterWithoutUserID: any = {
          deleted: { eq: "0" },
        };

        // Don't filter by conceptID here either - fetch all and filter client-side

        response = await API.graphql({
          query: listOrderedItems,
          variables: {
            filter: filterWithoutUserID,
            limit: 100, // Limit to avoid fetching too much
            nextToken: null,
          },
          authMode: session
            ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
            : GRAPHQL_AUTH_MODE.AWS_IAM,
        });

        listing = response.data.listOrderedItems.items;

        // Filter by userID and isGlobal client-side
        const beforeUserFilter = listing.length;
        listing = listing.filter(
          (item: any) =>
            item.userID === params.userId &&
            (item.isGlobal === "1" ||
              item.isGlobal === null ||
              item.isGlobal === undefined),
        );
      } else {
        // Filter by isGlobal client-side (prefer "1", but also include null/undefined if no "1" items exist)
        const beforeIsGlobalFilter = listing.length;
        const globalItems = listing.filter(
          (item: any) => item.isGlobal === "1",
        );


        if (globalItems.length > 0) {
          listing = globalItems;
        } else {
  
        }
        // If no items with isGlobal="1", use all items (they might not have isGlobal set)
      }

      // Filter by conceptID client-side if we have multiple concept filters
      if (conceptsSelectedFilters && conceptsSelectedFilters.length > 0) {
        const beforeConceptFilter = listing.length;
        const conceptIds = conceptsSelectedFilters.map((f: any) => f.id);

        listing = listing.filter((item: any) => {
          // For global items (isGlobal="1"), include them regardless of conceptID
          // For non-global items, filter by conceptID
          if (item.isGlobal === "1") {
            return true;
          }
          return item.conceptID && conceptIds.includes(item.conceptID);
        });
      } else if (params.conceptID) {
        // Also filter by single conceptID if provided (client-side for consistency)
        // But allow global items (isGlobal="1") to pass through regardless of conceptID
        const beforeConceptFilter = listing.length;

        listing = listing.filter((item: any) => {
          // For global items (isGlobal="1"), include them regardless of conceptID
          // For non-global items, filter by conceptID
          if (item.isGlobal === "1") {
            return true;
          }
          return item.conceptID === params.conceptID;
        });
      }

      // Sort by count descending (client-side since GraphQL doesn't support sort)
      listing = listing.sort((a: any, b: any) => {
        const countA = a.count || 0;
        const countB = b.count || 0;
        return countB - countA;
      });

      // Apply pagination client-side
      if (params.startIndex > 0 || params.limit) {
        const start = params.startIndex || 0;
        const end = params.limit ? start + params.limit : undefined;
        listing = listing.slice(start, end);
      }

      return listing;
    } catch (err: Error | any) {
      showError(err);
      return [];
    }
  }

  async function fetch(props: ListingVariables) {
    const { searchText, startIndex, limit, fromDate, toDate } = props;

    try {
      const filter: any = {
        deleted: { eq: "0" },
        userID: { eq: GLOBAL_GUEST_ID },
      };

      // Note: We don't filter by date in GraphQL query for ordered items
      // because updatedAt might not be indexed, and we want to show items
      // that were updated (had new orders) during the date range.
      // Date filtering will be done client-side after fetching.

      // Handle conceptID filtering
      if (
        conceptsSelected &&
        !(conceptsSelectedFilters && conceptsSelectedFilters.length > 0)
      ) {
        filter.conceptID = { eq: conceptsSelected };
      } else if (
        conceptsSelectedFilters &&
        conceptsSelectedFilters.length > 0
      ) {
        const orConditions = conceptsSelectedFilters.map((filterItem: any) => ({
          conceptID: { eq: filterItem.id },
        }));
        filter.or = orConditions;
      }

      // Handle search text
      if (searchText && searchText.length > 0) {
        if (filter.or) {
          filter.or.push({ itemName: { contains: searchText } });
        } else {
          filter.itemName = { contains: searchText };
        }
      }

      const response: any = await API.graphql({
        query: listOrderedItems,
        variables: {
          filter,
          limit: limit || 10000,
          nextToken: null,
        },
        authMode: session
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      let listing = response.data.listOrderedItems.items;

      // Filter by date client-side (using updatedAt to show items with activity in date range)
      if (fromDate && toDate) {
        const fromDateEdited = new Date(fromDate);
        fromDateEdited.setHours(0, 0, 0, 0);
        const fromTimestamp = fromDateEdited.getTime();

        const toDatePlusOneDay = new Date(toDate);
        toDatePlusOneDay.setDate(toDatePlusOneDay.getDate() + 1);
        toDatePlusOneDay.setHours(0, 0, 0, 0);
        const toTimestamp = toDatePlusOneDay.getTime();

        listing = listing.filter((item: any) => {
          // Use updatedAt if available, otherwise fall back to createdAt
          const itemDate =
            item.updatedAt || item.createdAt || item._lastChangedAt;
          if (!itemDate) return false;

          const itemTimestamp = new Date(itemDate).getTime();
          return itemTimestamp >= fromTimestamp && itemTimestamp < toTimestamp;
        });
      }

      // Sort by count descending (client-side since GraphQL doesn't support sort)
      listing = listing.sort((a: any, b: any) => {
        const countA = a.count || 0;
        const countB = b.count || 0;
        return countB - countA;
      });

      // Apply pagination client-side
      if (startIndex > 0 || limit) {
        const start = startIndex || 0;
        const end = limit ? start + limit : undefined;
        listing = listing.slice(start, end);
      }

      return listing;
    } catch (err: Error | any) {
      showError(err);
      return [];
    }
  }

  async function fetchOrdered(props: ListingVariables) {
    try {
      const { searchText, startIndex, limit } = props;

      const filter: any = {
        deleted: { eq: "0" },
        userID: { eq: GLOBAL_GUEST_ID },
      };

      // Handle conceptID filtering
      if (
        conceptsSelected &&
        !(conceptsSelectedFilters && conceptsSelectedFilters.length > 0)
      ) {
        filter.conceptID = { eq: conceptsSelected };
      } else if (
        conceptsSelectedFilters &&
        conceptsSelectedFilters.length > 0
      ) {
        const orConditions = conceptsSelectedFilters.map((filterItem: any) => ({
          conceptID: { eq: filterItem.id },
        }));
        filter.or = orConditions;
      }

      // Handle search text
      if (searchText && searchText.length > 0) {
        if (filter.or) {
          filter.or.push({ itemName: { contains: searchText } });
        } else {
          filter.itemName = { contains: searchText };
        }
      }

      const response: any = await API.graphql({
        query: listOrderedItems,
        variables: {
          filter,
          limit: limit || 10000,
          nextToken: null,
        },
        authMode: session
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      let listing = response.data.listOrderedItems.items;
      // Sort by count descending (client-side since GraphQL doesn't support sort)
      listing = listing.sort((a: any, b: any) => {
        const countA = a.count || 0;
        const countB = b.count || 0;
        return countB - countA;
      });

      // Apply pagination client-side
      if (startIndex > 0 || limit) {
        const start = startIndex || 0;
        const end = limit ? start + limit : undefined;
        listing = listing.slice(start, end);
      }

      return listing;
    } catch (err: Error | any) {
      showError(err);
      return [];
    }
  }

  async function exportAll(params: any) {
    try {
      let exportedData = [];

      const data = await fetch(params);

      for (let user of data!) {
        let row: any = { ...user };
        if (user.group) {
          row.groupName = groupsGetName(user.group);
        }
        exportedData.push(row);
      }

      return exportedData;
    } catch (err) {
      showError(err);
    }
  }

  const headCells: readonly HeadCell[] = [
    {
      id: "itemName",
      numeric: false,
      disablePadding: false,
      label: "Item Name",
    },
    {
      id: "itemCount",
      numeric: false,
      disablePadding: false,
      label: "Sold Count",
    },
    {
      id: "createdBy",
      numeric: false,
      disablePadding: false,
      label: "Created By",
    },
    {
      id: "createdAt",
      numeric: false,
      disablePadding: false,
      label: "Create Date",
    },
    {
      id: "actions",
      numeric: true,
      disablePadding: false,
      label: "",
    },
  ];

  const dataCells: readonly string[] = ["itemName", "count"];

  const api: any = {};
  api[`${listingName}Listing`] = listing;
  api[`${listingName}SearchText`] = searchText;
  api[`${listingName}DataCells`] = dataCells;
  api[`${listingName}HeadCells`] = headCells;
  api[`${listingName}ListTopCustomers`] = listTopCustomers;
  api[`${listingName}ListTopOrderedItems`] = listTopOrderedItems;
  api[`${listingName}ListUserOrderedItemsCount`] = listUserOrderedItemsCount;
  api[`${listingName}Fetch`] = fetch;
  api[`${listingName}FetchOrdered`] = fetchOrdered;
  api[`${listingName}Export`] = exportAll;

  api[`${listingName}ChangeListing`] = (listing: any[]) =>
    dispatch(setListing(listing));
  api[`${listingName}Search`] = (searchText: string) =>
    dispatch(setSearchText(searchText));
  api[`${listingName}Select`] = (orderedItemID: string) =>
    dispatch(setSelected(orderedItemID));

  return api;
};
export default useResource;
